import { createBasePDF } from '@/utils/pdfConfig';
import { drawHeader } from '@/utils/pdfHeader';
import { drawFooter } from '@/utils/pdfFooter';
import { translateTxType } from '@/utils/dictionary';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';

export function useDisbursementVoucher() {

  const formatMoney = (amount) => {
    return Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const user = SecureStorage.getItem('llevo_user');

  const loadImage = (url) => {
    return new Promise((resolve) => {
      if (!url) return resolve(null);
      const proxiedUrl = url.replace('https://frontline.therealdevs.pro', '/api-proxy');
      const img = new Image();
      img.setAttribute('crossOrigin', 'anonymous');
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = proxiedUrl;
    });
  };

  const generateVoucher = async (dossier, displayOptions = {}, globalCatalog = []) => {
    const { loan, customer, product, deductions = [] } = dossier;
    const nombreUsuario = user?.full_name || 'SISTEMA';

    // Helper global homologado
    const getGlobalText = (value, type) => {
      if (!globalCatalog || globalCatalog.length === 0) return String(value || 'N/D');
      const item = globalCatalog.find(g => Number(g.type) === type && String(g.value) === String(value));
      return item ? item.text.toUpperCase() : String(value || 'N/D');
    };

    try {
      const configRes = await apiClient.get('/company_config');
      const config = Array.isArray(configRes.data) ? configRes.data[0] : configRes.data;
      const logoImg = await loadImage(config.logo_url);

      const doc = createBasePDF('p', 'letter');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);

      // --- ENCABEZADO ---
      const headerOptions = {
        companyName: config.trade_name,
        title: 'COMPROBANTE DE LIQUIDACIÓN DE DESEMBOLSO',
        subtitle: `Impreso el: ${new Date().toLocaleDateString('es-ES')} a las ${new Date().toLocaleTimeString('es-ES')} | Usuario: ${nombreUsuario}`,
        logo: logoImg,
        useColors: false
      };

      drawHeader(doc, headerOptions);
      let y = 45;

      let sectionCounter = 1;

      // --- MOTOR DE PAGINACIÓN HOMOLOGADO ---
      const checkPageBreak = (neededSpace) => {
        if (y + neededSpace > pageHeight - 30) {
          doc.addPage();
          drawHeader(doc, headerOptions);
          y = 45;
          return true;
        }
        return false;
      };

      // --- RENDERIZADORES HOMOLOGADOS ---
      const drawSectionTitle = (title) => {
        checkPageBreak(15);
        doc.setFillColor(245, 245, 245);
        doc.rect(margin, y, contentWidth, 7, 'F');
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.3);
        doc.line(margin, y, pageWidth - margin, y);
        doc.line(margin, y + 7, pageWidth - margin, y + 7);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        doc.text(`${sectionCounter++}. ${title.toUpperCase()}`, margin + 2, y + 5);
        y += 12;
      };

      const drawLabelValue = (label, value, x, currentY) => {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(50, 50, 50);
        doc.text(label, x, currentY);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text(value ? String(value) : 'N/D', x + 30, currentY);
      };

      const drawLabelValueLong = (label, value, x, currentY, textMaxWidth) => {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(50, 50, 50);
        doc.text(label, x, currentY);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        const textLines = doc.splitTextToSize(value ? String(value) : 'N/D', textMaxWidth);
        doc.text(textLines, x + 30, currentY);
        return textLines.length * 4.5;
      };

      // ====================================================================
      // 1. PERFIL DEL CLIENTE BENEFICIARIO (LIMPIO Y DIRECTO)
      // ====================================================================
      drawSectionTitle('Perfil del Cliente Beneficiario');
      doc.setFontSize(8);
      const col1X = margin;
      const col2X = margin + (contentWidth / 2) + 5;
      let lineH = 5.5;

      drawLabelValue('Cód. Cliente:', customer.code, col1X, y);
      drawLabelValue('Doc. Identidad:', customer.identity_document, col2X, y);
      y += lineH;

      drawLabelValue('Nombre:', customer.full_name, col1X, y);
      drawLabelValue('Género:', customer.gender === 'M' ? 'MASCULINO' : (customer.gender === 'F' ? 'FEMENINO' : 'N/D'), col2X, y);
      y += lineH;

      let offset = drawLabelValueLong('Dirección:', customer.address || 'N/D', col1X, y, contentWidth - 40);
      y += offset + 2;


      // ====================================================================
      // 2. RESOLUCIÓN DEL CRÉDITO (CON FRECUENCIA DINÁMICA Y CUOTAS)
      // ====================================================================
      drawSectionTitle('Resolución y Detalles del Crédito');
      doc.setFontSize(8);

      drawLabelValue('Cód. Crédito:', loan.code, col1X, y);
      drawLabelValue('Producto:', product?.name?.toUpperCase() || 'N/D', col2X, y);
      y += lineH;

      const nombreEjecutivo = loan.analista || loan.adviser || 'N/D';
      drawLabelValue('Ejecutivo:', String(nombreEjecutivo).toUpperCase(), col1X, y);
      drawLabelValue('Estado:', translateTxType(loan.status)?.toUpperCase(), col2X, y);
      y += lineH;

      drawLabelValue('Monto Aprobado:', `$ ${formatMoney(loan.amount)}`, col1X, y);
      drawLabelValue('Monto Total(c/int):', `$ ${formatMoney(loan.total_amount)}`, col2X, y);
      y += lineH;

      // FRECUENCIA DINÁMICA Y PLAZO EN CUOTAS
      const frecuencia = translateTxType(loan.condition_level)?.toUpperCase() || 'N/D';
      drawLabelValue('Plazo Autorizado:', `${loan.term} CUOTAS`, col1X, y);
      drawLabelValue('Frecuencia:', frecuencia, col2X, y);
      y += lineH;

      drawLabelValue('Tasa Interés:', `${loan.rate}%`, col1X, y);
      drawLabelValue('Valor Cuota:', `$ ${formatMoney(loan.estimated_quota)}`, col2X, y);
      y += lineH;

      drawLabelValue('Vigencia Desde:', loan.init_date, col1X, y);
      drawLabelValue('Vencimiento:', loan.end_date, col2X, y);
      y += lineH;

      drawLabelValue('Vía Desembolso:', loan.disbursement_method?.toUpperCase() || 'N/D', col1X, y);
      drawLabelValue('Notas Desemb.:', loan.disbursement_notes?.toUpperCase() || 'N/D', col2X, y);
      y += lineH + 4;


      // ====================================================================
      // 3. DESGLOSE DE DEDUCCIONES Y LIQUIDACIÓN
      // ====================================================================
      drawSectionTitle('Desglose de Deducciones y Líquido a Entregar');

      let sumaDeducciones = 0;

      if (!deductions || deductions.length === 0) {
        doc.setFont('helvetica', 'italic');
        doc.text('El crédito no presenta cargos o deducciones por desembolso.', margin, y);
        y += lineH + 4;
      } else {
        doc.setFont('helvetica', 'bold');
        doc.text('CONCEPTO DE DEDUCCIÓN', margin, y);
        doc.text('MONTO', pageWidth - margin, y, { align: 'right' });
        y += 2;
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.1);
        doc.line(margin, y, pageWidth - margin, y);
        y += 5;

        doc.setFont('helvetica', 'normal');
        deductions.forEach(d => {
          checkPageBreak(8);
          doc.text(d.name?.toUpperCase() || 'CARGO ADMINISTRATIVO', margin, y);
          doc.text(`- $ ${formatMoney(d.amount)}`, pageWidth - margin, y, { align: 'right' });
          sumaDeducciones += Number(d.amount);
          y += 5;
        });
        y += 2;
      }

      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.3);
      doc.line(margin, y, pageWidth - margin, y);
      y += 6;

      doc.setFont('helvetica', 'bold');
      doc.text('TOTAL DEDUCCIONES:', margin + 110, y, { align: 'right' });
      doc.text(`$ ${formatMoney(sumaDeducciones)}`, pageWidth - margin, y, { align: 'right' });
      y += 8;

      const liquidoTotal = Number(loan.amount) - sumaDeducciones;

      checkPageBreak(25);
      doc.setFillColor(0, 0, 0);
      doc.rect(margin, y, contentWidth, 12, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      doc.text('LÍQUIDO A ENTREGAR:', margin + 3, y + 8);
      doc.text(`$ ${formatMoney(liquidoTotal)}`, pageWidth - margin - 3, y + 8, { align: 'right' });

      y += 40;


      // ====================================================================
      // 4. ÁREA DE FIRMAS Y AUTORIZACIONES
      // ====================================================================
      checkPageBreak(30);

      doc.setTextColor(0, 0, 0);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');

      const signatureWidth = 45;
      const gap = (contentWidth - (signatureWidth * 3)) / 2;

      const sig1X = margin;
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.3);
      doc.line(sig1X, y, sig1X + signatureWidth, y);
      doc.setFont('helvetica', 'bold');
      doc.text('ELABORADO POR', sig1X + (signatureWidth / 2), y + 4, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.text(nombreUsuario, sig1X + (signatureWidth / 2), y + 8, { align: 'center' });

      const sig2X = sig1X + signatureWidth + gap;
      doc.line(sig2X, y, sig2X + signatureWidth, y);
      doc.setFont('helvetica', 'bold');
      doc.text('AUTORIZADO POR', sig2X + (signatureWidth / 2), y + 4, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.text(loan.approved_by || 'GERENCIA', sig2X + (signatureWidth / 2), y + 8, { align: 'center' });

      const sig3X = pageWidth - margin - signatureWidth;
      doc.line(sig3X, y, sig3X + signatureWidth, y);
      doc.setFont('helvetica', 'bold');
      doc.text('RECIBE CONFORME', sig3X + (signatureWidth / 2), y + 4, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.text(customer.full_name?.substring(0, 25), sig3X + (signatureWidth / 2), y + 8, { align: 'center' });
      doc.text(`ID: ${customer.identity_document || 'N/D'}`, sig3X + (signatureWidth / 2), y + 12, { align: 'center' });


      // ====================================================================
      // FOOTER FINAL
      // ====================================================================
      const totalPages = doc.internal.getNumberOfPages();

      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        drawFooter(doc, { companyName: config.trade_name });
      }

      doc.save(`Comprobante_Desembolso_${loan.code}.pdf`);

    } catch (error) {
      console.error('Error generando liquidación de desembolso:', error);
    }
  };

  return { generateVoucher };
}